import { resolveEffectiveVersionBounds } from '@lifeserver/shared';

const VERSION_PATTERN = /^\s*v?(\d+)(?:\.(\d+))?(?:\.(\d+))?/;

// Re-exported so ScheduleView/EventsView/tests import it from here alongside
// the rest of the version-range helpers, without duplicating the resolution
// logic that already lives once in @lifeserver/shared (shared with the server).
export { resolveEffectiveVersionBounds };

function parseVersionParts(
    version: string,
): { major: number; minor: number; patch: number } | null {
    const match = VERSION_PATTERN.exec(version);
    if (!match) return null;

    return {
        major: Number(match[1]),
        minor: Number(match[2] ?? 0),
        patch: Number(match[3] ?? 0),
    };
}

/** Encodes a semver-ish string so ranges can be compared and merged numerically. */
export function toComparable(version: string): number | null {
    const parts = parseVersionParts(version);
    if (!parts) return null;

    return parts.major * 1e12 + parts.minor * 1e6 + parts.patch;
}

/**
 * When a range spans exactly one clean minor or major line — e.g.
 * `[1.39.0, 1.40.0)` or `[1.0.0, 2.0.0)` — that's more readable as
 * "Only 1.39.x" than as a from/to range.
 */
function formatAsSingleLine(
    minimumVersion: string,
    maximumVersion: string,
): string | null {
    const min = parseVersionParts(minimumVersion);
    const max = parseVersionParts(maximumVersion);
    if (!min || !max) return null;

    if (min.patch === 0 && max.patch === 0) {
        if (min.major === max.major && max.minor === min.minor + 1) {
            return `Only ${min.major}.${min.minor}.x`;
        }
        if (min.minor === 0 && max.minor === 0 && max.major === min.major + 1) {
            return `Only ${min.major}.x`;
        }
    }

    return null;
}

export function formatVersionRange(
    minimumVersion?: string | null,
    maximumVersion?: string | null,
): string {
    // "inclusive"/"exclusive" match the wording used in the server's validation
    // errors and the authoring form's placeholders, so the concept reads the
    // same everywhere in the product.
    if (minimumVersion && maximumVersion) {
        return (
            formatAsSingleLine(minimumVersion, maximumVersion) ??
            `${minimumVersion} up to ${maximumVersion} (exclusive)`
        );
    }
    if (minimumVersion) return `${minimumVersion} and up`;
    if (maximumVersion) return `Below ${maximumVersion}`;
    return 'All versions';
}

export function hasVersionBounds(item: {
    minimumVersion?: string | null;
    maximumVersion?: string | null;
}): boolean {
    return Boolean(item.minimumVersion || item.maximumVersion);
}

type Interval = { from: number; to: number };

function toInterval(item: {
    minimumVersion?: string | null;
    maximumVersion?: string | null;
}): Interval {
    const from = item.minimumVersion
        ? (toComparable(item.minimumVersion) ?? -Infinity)
        : -Infinity;
    const to = item.maximumVersion
        ? (toComparable(item.maximumVersion) ?? Infinity)
        : Infinity;
    return { from, to };
}

type RawInterval = Interval & {
    fromVersion: string | null;
    toVersion: string | null;
};

function toRawInterval(item: {
    minimumVersion?: string | null;
    maximumVersion?: string | null;
}): RawInterval {
    return {
        ...toInterval(item),
        fromVersion: item.minimumVersion ?? null,
        toVersion: item.maximumVersion ?? null,
    };
}

/**
 * Finds the first stretch of client versions that none of the given items
 * cover, if any. Returns `null` when the items together cover everything.
 */
function findFirstGap(items: RawInterval[]): RawInterval | null {
    if (items.length === 0) {
        return {
            from: -Infinity,
            to: Infinity,
            fromVersion: null,
            toVersion: null,
        };
    }

    const sorted = [...items].sort((a, b) => a.from - b.from || a.to - b.to);

    if (sorted[0].from !== -Infinity) {
        return {
            from: -Infinity,
            to: sorted[0].from,
            fromVersion: null,
            toVersion: sorted[0].fromVersion,
        };
    }

    let reach = sorted[0].to;
    let reachVersion = sorted[0].toVersion;
    for (const interval of sorted.slice(1)) {
        if (reach === Infinity) return null;
        if (interval.from > reach) {
            return {
                from: reach,
                to: interval.from,
                fromVersion: reachVersion,
                toVersion: interval.fromVersion,
            };
        }
        if (interval.to > reach) {
            reach = interval.to;
            reachVersion = interval.toVersion;
        }
    }

    if (reach === Infinity) return null;
    return {
        from: reach,
        to: Infinity,
        fromVersion: reachVersion,
        toVersion: null,
    };
}

/**
 * Returns true when the given items together leave no client version uncovered.
 *
 * Windows are half-open `[min, max)`, so complementary ranges that share a
 * boundary tile exactly and need no tolerance.
 */
export function coversAllVersions(
    items: Array<{
        minimumVersion?: string | null;
        maximumVersion?: string | null;
    }>,
): boolean {
    return findFirstGap(items.map(toRawInterval)) === null;
}

/**
 * Describes the first uncovered stretch of client versions in human terms,
 * so a coverage warning can say *which* clients are affected instead of just
 * that a gap exists. Returns `null` when there is no gap.
 */
export function describeVersionGap(
    items: Array<{
        minimumVersion?: string | null;
        maximumVersion?: string | null;
    }>,
): string | null {
    const gap = findFirstGap(items.map(toRawInterval));
    if (!gap) return null;

    if (gap.fromVersion && gap.toVersion) {
        return `${gap.fromVersion} up to ${gap.toVersion}`;
    }
    if (gap.toVersion) return `below ${gap.toVersion}`;
    if (gap.fromVersion) return `${gap.fromVersion} and up`;
    return 'every client version';
}
